import { useCallback, useEffect, useState } from 'react';

/**
 * A hook that returns a debounced function of the provided promise. The debounced function will postpone the execution of the promise until after `delay` milliseconds have elapsed since the last time it was invoked.
 *
 * @template T - The type of the value resolved by the promise.
 * @param {() => Promise<T>} promiseFn - The promise function that you want to debounce.
 * @param {number} delay - The number of milliseconds to delay the promise invocation.
 * @returns {() => Promise<T>} A debounced version of the promise function.
 *
 * @example
 * function fetchData() {
 *   // Function that returns a promise
 * }
 *
 * const debouncedFetchData = useDebouncedPromise(fetchData, 500);
 *
 * // Use the `debouncedFetchData` in your code
 * // It will only be called 500ms after the last invocation
 * debouncedFetchData().then((data) => {
 *   // Handle the resolved promise value
 * });
 */

type TimeoutProps = ReturnType<typeof setTimeout>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebouncedPromise<T>(promiseFn: (...args: any[]) => Promise<T>, delay: number): () => Promise<T> {
  // Internal state to keep track of the timeout ID
  const [timeoutId, setTimeoutId] = useState<TimeoutProps | null>(null);

  // The debounced function
  const debouncedPromise = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...args: any[]) =>
      new Promise<T>((resolve, reject) => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        const id = setTimeout(() => {
          promiseFn(...args)
            .then(resolve)
            .catch(reject);
        }, delay);
        setTimeoutId(id);
      }),
    [promiseFn, delay, timeoutId]
  );

  // Clean up the timeout on unmount
  useEffect(
    () => () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    },
    [timeoutId]
  );

  return debouncedPromise;
}
